import { useMemo } from "react";
import { toast } from "sonner";
import { EyeOff } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { useHomey } from "@/hooks/useHomey";
import { normalizeHiddenSettings } from "@/lib/hidden-devices";
import type { HomeyDeviceSnap } from "@/types/homey";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

/** A zone group of devices for the visibility list, sorted by device name. */
interface ZoneGroup {
  zoneId: string;
  zoneName: string;
  devices: HomeyDeviceSnap[];
}

/**
 * DeviceVisibilitySection — "Device visibility" settings card.
 *
 * Lists every device from the live Homey snapshot, grouped by zone, with a
 * per-device toggle. A device toggled OFF (not "shown") is hidden from the Home
 * dashboard. Persists the full id list on each change via setHiddenDevices and
 * invalidates the dashboard-settings query so the Home view updates.
 *
 * To test: toggle a device off, go to the Home tab and confirm it disappears
 * from its zone; reload Settings and confirm the toggle state persisted.
 */
export function DeviceVisibilitySection() {
  const utils = trpc.useUtils();
  const { snapshot } = useHomey();
  const { data: settingsRow } = trpc.homey.getDashboardSettings.useQuery();

  const hiddenIds = useMemo(
    () => new Set(normalizeHiddenSettings(settingsRow?.hidden).deviceIds),
    [settingsRow],
  );

  // Devices grouped by zone, zones and devices both sorted by name.
  const groups = useMemo<ZoneGroup[]>(() => {
    if (!snapshot) return [];
    const zoneName = new Map(snapshot.zones.map((z) => [z.id, z.name]));
    const byZone = new Map<string, HomeyDeviceSnap[]>();
    for (const device of snapshot.devices) {
      const list = byZone.get(device.zoneId) ?? [];
      list.push(device);
      byZone.set(device.zoneId, list);
    }
    return [...byZone.entries()]
      .map(([zoneId, devices]) => ({
        zoneId,
        zoneName: zoneName.get(zoneId) ?? "Unknown zone",
        devices: [...devices].sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => a.zoneName.localeCompare(b.zoneName));
  }, [snapshot]);

  const save = trpc.homey.setHiddenDevices.useMutation({
    onSuccess: () => {
      void utils.homey.getDashboardSettings.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  // Toggle a single device's visibility, then persist the full new hidden list.
  const setVisible = (deviceId: string, visible: boolean) => {
    const next = new Set(hiddenIds);
    if (visible) next.delete(deviceId);
    else next.add(deviceId);
    save.mutate({ deviceIds: [...next] });
  };

  return (
    <Card data-slot="device-visibility-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <EyeOff className="size-4" /> Device visibility
        </CardTitle>
        <CardDescription>
          Hide devices you do not want on the Home dashboard. Hidden devices stay connected to
          Homey, they just no longer appear in their zone or count towards the glance totals.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        {!snapshot || groups.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No devices to show yet. Connect your Homey and wait for it to load, then the devices
            will appear here.
          </p>
        ) : (
          groups.map((group) => (
            <div key={group.zoneId} className="flex flex-col gap-3">
              <h3 className="text-xs font-medium tracking-widest text-muted-foreground uppercase">
                {group.zoneName}
              </h3>
              <div className="flex flex-col gap-3">
                {group.devices.map((device) => {
                  const visible = !hiddenIds.has(device.id);
                  const switchId = `device-visible-${device.id}`;
                  return (
                    <div key={device.id} className="flex items-center justify-between gap-4">
                      <Label htmlFor={switchId} className="min-w-0 flex-1 truncate font-normal">
                        {device.name}
                      </Label>
                      <Switch
                        id={switchId}
                        checked={visible}
                        disabled={save.isPending}
                        onCheckedChange={(checked) => setVisible(device.id, checked)}
                        aria-label={`Show ${device.name} on the dashboard`}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

export default DeviceVisibilitySection;
